import { createConfirmCancelButtons } from './modal-footer-buttons';
import { EventSnippet, Snippet } from '../../interop/webmodule-interop';
import { ApiError, ProblemTypes } from '../../interop/problem-details';
import type { TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { LitBaseModal } from './modal/modal-factory-lit';
import { MultiPromise } from '../general/multicast-promise';
import { LangModalErrorHandler } from '../../../localization/webmodule-common/other/ui/modal-errorhandler-lang';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * Sanitizes sensitive information from error text (stack traces, details, etc.)
 * Removes or masks authorization tokens, API keys, and other sensitive headers
 */
function sanitizeSensitiveData(text: string): string {
  if (!text) return text;

  // Patterns for sensitive data that should be redacted
  const sensitivePatterns: [RegExp, string][] = [
    // Authorization header with bearer token
    [/Authorization:\s*bearer\s+[A-Za-z0-9+/=]+/gi, 'Authorization: bearer [REDACTED]'],
    // Generic Authorization header
    [/Authorization:\s*[^\n\r]+/gi, 'Authorization: [REDACTED]'],
    // authorization-client header
    [/authorization-client:\s*[^\n\r]+/gi, 'authorization-client: [REDACTED]'],
    // API keys in headers or query strings
    [/api[_-]?key[=:]\s*[A-Za-z0-9\-_]+/gi, 'api-key: [REDACTED]'],
    // Bearer tokens in general
    [/bearer\s+[A-Za-z0-9+/=]{20,}/gi, 'bearer [REDACTED]'],
    // Connection strings
    [/(?:Password|Pwd)\s*=\s*[^;"\s]+/gi, 'Password=[REDACTED]'],
    // Access tokens
    [/access[_-]?token[=:]\s*[A-Za-z0-9\-_\.]+/gi, 'access_token=[REDACTED]'],
    // Refresh tokens
    [/refresh[_-]?token[=:]\s*[A-Za-z0-9\-_\.]+/gi, 'refresh_token=[REDACTED]'],
    // Client secrets
    [/client[_-]?secret[=:]\s*[A-Za-z0-9\-_\.]+/gi, 'client_secret=[REDACTED]']
  ];

  let sanitized = text;
  for (const [pattern, replacement] of sensitivePatterns) {
    sanitized = sanitized.replace(pattern, replacement);
  }

  return sanitized;
}

/**
 * Detects if the stack trace is from a .NET/C# server or JavaScript
 */
function isServerStackTrace(stackTrace: string): boolean {
  // C# stack traces typically contain "at namespace.Class.Method" patterns
  // and file paths like "in D:\...\file.cs:line 123"
  return (
    stackTrace.includes('.cs:line') ||
    stackTrace.includes('Microsoft.AspNetCore') ||
    stackTrace.includes('System.') ||
    /at\s+[\w.]+\(.*\)\s+in\s+.*:\s*line\s+\d+/i.test(stackTrace)
  );
}

interface ParsedStackFrame {
  method: string;
  file?: string;
  line?: number;
  isInternal?: boolean;
}

interface ParsedServerError {
  exceptionType: string;
  message: string;
  parameterName?: string;
  innerException?: ParsedServerError;
  frames: ParsedStackFrame[];
}

/**
 * Parses a .NET/C# server stack trace into structured data
 */
function parseServerStackTrace(stackTrace: string): ParsedServerError {
  const lines = stackTrace
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);

  // Parse the first line for exception type and message
  // Format: "System.ArgumentException: Test exception message (Parameter 'paramName')"
  const firstLine = lines[0] || '';
  let exceptionType = 'Exception';
  let message = firstLine;
  let parameterName: string | undefined;
  let innerException: ParsedServerError | undefined;

  const exceptionMatch = firstLine.match(/^([\w.]+Exception):\s*(.*)$/);
  if (exceptionMatch) {
    exceptionType = exceptionMatch[1];
    message = exceptionMatch[2];

    // Extract parameter name if present
    const paramMatch = message.match(/\(Parameter '(\w+)'\)$/);
    if (paramMatch) {
      parameterName = paramMatch[1];
      message = message.replace(/\s*\(Parameter '\w+'\)$/, '');
    }
  }

  // Find inner exception
  const innerExceptionIndex = lines.findIndex(l => l.startsWith('--->'));
  if (innerExceptionIndex > 0) {
    const innerLine = lines[innerExceptionIndex].replace(/^--->/, '').trim();
    const innerMatch = innerLine.match(/^([\w.]+Exception):\s*(.*)$/);
    if (innerMatch) {
      innerException = {
        exceptionType: innerMatch[1],
        message: innerMatch[2],
        frames: []
      };
    }
  }

  // Parse stack frames
  const frames: ParsedStackFrame[] = [];
  for (const line of lines) {
    // Skip non-frame lines
    if (!line.startsWith('at ')) continue;

    // Parse frame: "at Namespace.Class.Method(params) in D:\path\file.cs:line 123"
    const frameMatch = line.match(/^at\s+([\w.<>]+)\((.*?)\)(?:\s+in\s+(.+?):line\s+(\d+))?/);
    if (frameMatch) {
      const method = frameMatch[1];
      const file = frameMatch[3];
      const lineNum = frameMatch[4] ? parseInt(frameMatch[4]) : undefined;

      // Determine if this is internal framework code
      const isInternal =
        method.startsWith('Microsoft.') || method.startsWith('System.') || method.includes('lambda_method');

      frames.push({ method, file, line: lineNum, isInternal });
    }
  }

  return { exceptionType, message, parameterName, innerException, frames };
}

/**
 * Renders a formatted server error with collapsible stack trace
 */
function renderServerError(stackTrace: string): TemplateResult {
  const parsed = parseServerStackTrace(stackTrace);

  // Get only the user code frames (non-internal)
  const userFrames = parsed.frames.filter(f => !f.isInternal);
  const internalFrames = parsed.frames.filter(f => f.isInternal);

  const renderFrame = (frame: ParsedStackFrame, index: number): TemplateResult => {
    const methodParts = frame.method.split('.');
    const methodName = methodParts.pop() || frame.method;
    const namespace = methodParts.join('.');

    const fileInfo = frame.file
      ? html`<span class="stack-file">${frame.file.split('\\').pop()}:${frame.line}</span>`
      : nothing;

    return html`
      <div class="stack-frame ${frame.isInternal ? 'stack-frame-internal' : 'stack-frame-user'}">
        <span class="stack-index">${index + 1}.</span>
        <span class="stack-namespace">${namespace}.</span><span class="stack-method">${methodName}</span>
        ${fileInfo}
      </div>
    `;
  };

  const innerExceptionHtml = parsed.innerException
    ? html`
        <div class="inner-exception">
          <strong>⮕ Inner:</strong>
          <span class="exception-type">${parsed.innerException.exceptionType}</span>
          <span class="exception-message">${parsed.innerException.message}</span>
        </div>
      `
    : nothing;

  return html`
    <style>
      .server-error-container {
        font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
        font-size: 0.85rem;
        background: var(--bs-dark-bg-subtle, #f8f9fa);
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 0.375rem;
        padding: 1rem;
        margin-top: 0.5rem;
      }
      .exception-header {
        margin-bottom: 0.75rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid var(--bs-border-color, #dee2e6);
      }
      .exception-type {
        color: var(--bs-danger, #dc3545);
        font-weight: 600;
      }
      .exception-message {
        color: var(--bs-body-color, #212529);
        margin-left: 0.5rem;
      }
      .exception-param {
        color: var(--bs-warning, #ffc107);
        font-size: 0.8rem;
        margin-left: 0.5rem;
      }
      .inner-exception {
        background: var(--bs-warning-bg-subtle, #fff3cd);
        padding: 0.5rem;
        border-radius: 0.25rem;
        margin-top: 0.5rem;
        font-size: 0.8rem;
      }
      .stack-section-title {
        font-weight: 600;
        margin: 0.75rem 0 0.5rem 0;
        color: var(--bs-secondary-color, #6c757d);
        font-size: 0.8rem;
        text-transform: uppercase;
      }
      .stack-frame {
        padding: 0.25rem 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        align-items: baseline;
      }
      .stack-frame-user {
        background: var(--bs-body-bg, #fff);
        padding: 0.5rem;
        margin: 0.25rem 0;
        border-radius: 0.25rem;
        border-left: 3px solid var(--bs-primary, #0d6efd);
      }
      .stack-frame-internal {
        color: var(--bs-secondary-color, #6c757d);
        font-size: 0.75rem;
      }
      .stack-index {
        color: var(--bs-secondary-color, #6c757d);
        min-width: 1.5rem;
      }
      .stack-namespace {
        color: var(--bs-secondary-color, #6c757d);
      }
      .stack-method {
        color: var(--bs-primary, #0d6efd);
        font-weight: 600;
      }
      .stack-file {
        color: var(--bs-success, #198754);
        margin-left: auto;
        font-size: 0.75rem;
      }
      .stack-toggle {
        cursor: pointer;
        color: var(--bs-primary, #0d6efd);
        font-size: 0.8rem;
        margin-top: 0.5rem;
      }
      .stack-toggle:hover {
        text-decoration: underline;
      }
      .stack-internal-container {
        max-height: 200px;
        overflow-y: auto;
        border-top: 1px dashed var(--bs-border-color, #dee2e6);
        margin-top: 0.5rem;
        padding-top: 0.5rem;
      }
    </style>
    <div class="server-error-container">
      <div class="exception-header">
        <span class="exception-type">${parsed.exceptionType}</span>
        <span class="exception-message">${parsed.message}</span>
        ${parsed.parameterName
          ? html`<span class="exception-param">(Parameter: ${parsed.parameterName})</span>`
          : nothing}
        ${innerExceptionHtml}
      </div>

      ${userFrames.length > 0
        ? html`
            <div class="stack-section-title">📍 Your Code</div>
            ${userFrames.map((f, i) => renderFrame(f, i))}
          `
        : nothing}
      ${internalFrames.length > 0
        ? html`
            <details>
              <summary class="stack-toggle">📚 Framework Stack (${internalFrames.length} frames)</summary>
              <div class="stack-internal-container">
                ${internalFrames.map((f, i) => renderFrame(f, userFrames.length + i))}
              </div>
            </details>
          `
        : nothing}
    </div>
  `;
}

const formatText = (text?: string) => {
  if (!text) return;

  const lines = text.split('\n');

  const linesFormatted = lines
    .map(line => {
      // If there is a URL in the text we need to distinguish between single and double slashes
      // Split the URL into an array to distinguish double slashes from single slashes
      const doubleSlash = line.split('//');

      // Format the strings on either side of double slashes separately
      const formatted = doubleSlash
        .map(
          str =>
            // Insert a word break opportunity after a colon
            str
              .replace(/(?<after>:)/giu, '$1<wbr>')
              // Before a single slash (either direction), tilde, period, comma, hyphen, underline,
              // question mark, number sign, or percent symbol
              .replace(/(?<before>[/~.,\-_?#%])/giu, '<wbr>$1')
              // Before and after an equals sign or ampersand
              .replace(/(?<beforeAndAfter>[=&])/giu, '<wbr>$1<wbr>')
          // Reconnect the strings with word break opportunities after double slashes
        )
        .join('//<wbr>');

      return formatted;
    })
    .join('<br />');

  // eslint-disable-next-line consistent-return
  return linesFormatted;
};

let _modal: ErrorDialog | null = null;
let _title: EventSnippet = () => '';
let _message: Error | ApiError = new Error();

const _multicast = new MultiPromise<void>(async () => {
  _modal = new ErrorDialog();
  _modal.addError(_title, _message);
  await _modal.showModal();
  _modal = null;
});

export async function displayAllError(title: EventSnippet, message: Error | ApiError) {
  _title = title;
  _message = message;
  if (_modal) {
    _modal.addError(title, message);
  }
  return _multicast.run();
}

interface DialogError {
  title: EventSnippet;
  apiError: ApiError;
}

export class ErrorDialog extends LitBaseModal {
  private _errors: DialogError[] = [];
  public get errors(): DialogError[] {
    return this._errors;
  }
  public set errors(value: DialogError[]) {
    this._errors = value;
    this.requestUpdate();
  }
  errorCount = 0;

  private _displayCopiedButton = false;
  public get displayCopiedButton() {
    return this._displayCopiedButton;
  }
  public set displayCopiedButton(value) {
    this._displayCopiedButton = value;
    this.requestUpdate();
  }

  get eventTitle(): EventSnippet {
    return this.errors[0].title ?? (() => '');
  }
  get firstApiError(): ApiError {
    return this.errors[0].apiError;
  }
  async addError(title: EventSnippet, message: Error | ApiError) {
    this.errorCount++;
    let apiError: ApiError;

    if (message instanceof ApiError) {
      apiError = message;
    } else {
      // Convert plain Error to ApiError
      apiError = new ApiError({
        type: ProblemTypes.InternalError,
        title: 'Local UI Error',
        status: 0,
        detail: `${message.name}: ${message.message}`,
        stackTrace: message.stack
      });
    }

    const msg: DialogError = { title, apiError };
    if (!this.hasMatchingError(msg)) this.errors = [...this.errors, msg];
  }

  hasMatchingError(msg: DialogError) {
    return (
      this.errors.find(
        x => x.title === msg.title && x.apiError.problemDetails.detail === msg.apiError.problemDetails.detail
      ) !== undefined
    );
  }

  private getClipboardText(): string {
    const firstError = this.errors[0];
    const pd = firstError.apiError.problemDetails;
    const rawText = `Dealer Error (ProblemDetails):
Type: ${pd.type}
Status: ${pd.status}
Title: ${pd.title}
Detail: ${pd.detail}
Instance: ${pd.instance ?? 'N/A'}
Timestamp: ${pd.timestamp ?? 'N/A'}
-------------------------------------------------
${pd.stackTrace ?? ''}
    `;
    return sanitizeSensitiveData(rawText);
  }

  public footerTemplate(): TemplateResult {
    const copyEvent = () => {
      navigator.clipboard.writeText(this.getClipboardText());
      this.displayCopiedButton = true;

      setTimeout(() => {
        this.displayCopiedButton = false;
      }, 2000);
    };
    if (this.displayCopiedButton)
      return html` <webmodule-button size="small" variant="default" @click=${() => this.hideModal()}
          >${LangModalErrorHandler.close()}
        </webmodule-button>
        <webmodule-button size="small" variant="primary"
          >${LangModalErrorHandler.copiedToClipboard()}</webmodule-button
        >`;
    else
      return createConfirmCancelButtons(
        LangModalErrorHandler.copyToClipboard(),
        copyEvent,
        LangModalErrorHandler.close(),
        () => this.hideModal()
      );
  }

  get modalClasses(): string {
    return 'modal-dialog modal-dialog-scrollable';
  }

  get modalSize(): string {
    return 'modal-xl';
  }

  protected ZIndex(): number | undefined {
    return 99999;
  }

  title(): Snippet {
    return this.eventTitle();
  }

  private renderApiError(e: DialogError, i: number): TemplateResult {
    const pd = e.apiError.problemDetails;
    const retryBadge = pd.retryable ? html`<span class="badge bg-warning text-dark ms-2">Retryable</span>` : nothing;

    // Sanitize sensitive data from stack trace and detail
    const sanitizedStackTrace = pd.stackTrace ? sanitizeSensitiveData(pd.stackTrace) : undefined;
    const sanitizedDetail = pd.detail ? sanitizeSensitiveData(pd.detail) : pd.detail;

    // Check for validation errors in ProblemDetails
    const validationErrorsHtml = this.renderValidationErrors(pd.errors);
    const isValidationError =
      pd.status === 422 || pd.type.includes('validation-failure') || pd.type.includes('validation');

    // Determine error source and render stack trace appropriately
    const isServer = sanitizedStackTrace && isServerStackTrace(sanitizedStackTrace);
    const isJavaScript = pd.status === 0 || pd.title === 'Local UI Error';

    const sourceLabel = isJavaScript
      ? html`<span class="badge bg-secondary me-2">🖥️ Client</span>`
      : isValidationError
        ? html`<span class="badge bg-warning text-dark me-2">⚠️ Validation</span>`
        : html`<span class="badge bg-info me-2">☁️ Server</span>`;

    const stackTraceHtml = sanitizedStackTrace
      ? isServer
        ? renderServerError(sanitizedStackTrace)
        : html`<div class="alert alert-info mt-2" role="alert">
            <pre style="white-space: pre-wrap; margin: 0; font-size: 0.8rem;">${sanitizedStackTrace}</pre>
          </div>`
      : nothing;

    return html`
      <div class="alert ${isValidationError ? 'alert-warning' : 'alert-danger'}" role="alert">
        ${i > 0 ? html`<strong>${e.title()}</strong><br />` : nothing}
        <div class="d-flex align-items-center mb-2">
          ${sourceLabel}
          <span class="badge ${isValidationError ? 'bg-warning text-dark' : 'bg-danger'} me-2">HTTP ${pd.status}</span>
          <strong>${pd.title}</strong>
          ${retryBadge}
        </div>
        <div class="mb-2">${unsafeHTML(formatText(sanitizedDetail))}</div>
        ${validationErrorsHtml}
        <small class="text-muted">
          <strong>Type:</strong> ${pd.type}<br />
          ${pd.instance ? html`<strong>Endpoint:</strong> ${pd.instance}<br />` : nothing}
          ${pd.timestamp ? html`<strong>Time:</strong> ${pd.timestamp}<br />` : nothing}
        </small>
      </div>
      ${stackTraceHtml}
    `;
  }

  /**
   * Renders validation errors from ProblemDetails.errors field
   * Supports both { property: messages[] } format and ValidationError[] array format
   */
  private renderValidationErrors(errors: unknown): TemplateResult | typeof nothing {
    if (!errors) return nothing;

    const validationItems: { property: string; message: string }[] = [];

    // Handle Record<string, string[]> format (ASP.NET validation)
    if (typeof errors === 'object' && !Array.isArray(errors)) {
      const errorRecord = errors as Record<string, string[]>;
      for (const [property, messages] of Object.entries(errorRecord)) {
        if (Array.isArray(messages)) {
          for (const message of messages) {
            validationItems.push({ property, message });
          }
        }
      }
    }

    // Handle ValidationError[] array format (DealerValidationException)
    if (Array.isArray(errors)) {
      for (const err of errors) {
        if (typeof err === 'object' && err !== null && 'message' in err) {
          validationItems.push({
            property: (err as { property?: string }).property ?? '',
            message: (err as { message: string }).message
          });
        }
      }
    }

    if (validationItems.length === 0) return nothing;

    return html`
      <div class="validation-errors-container mt-2 mb-2">
        <style>
          .validation-errors-container {
            background: var(--bs-warning-bg-subtle, #fff3cd);
            border: 1px solid var(--bs-warning-border-subtle, #ffecb5);
            border-radius: 0.375rem;
            padding: 0.75rem;
          }
          .validation-errors-title {
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: var(--bs-warning-text-emphasis, #664d03);
          }
          .validation-error-item {
            padding: 0.25rem 0;
            border-bottom: 1px solid var(--bs-warning-border-subtle, #ffecb5);
          }
          .validation-error-item:last-child {
            border-bottom: none;
          }
          .validation-property {
            font-weight: 600;
            color: var(--bs-danger, #dc3545);
          }
          .validation-message {
            color: var(--bs-body-color, #212529);
          }
          .validation-arrow {
            color: var(--bs-secondary-color, #6c757d);
            margin: 0 0.5rem;
          }
        </style>
        <div class="validation-errors-title">⚠️ Validation Issues (${validationItems.length})</div>
        ${validationItems.map(
          item => html`
            <div class="validation-error-item">
              ${item.property
                ? html`<span class="validation-property">${item.property}</span><span class="validation-arrow">→</span>`
                : nothing}
              <span class="validation-message">${item.message}</span>
            </div>
          `
        )}
      </div>
    `;
  }

  public bodyTemplate() {
    return html` <h3>${LangModalErrorHandler.pleaseSubmitTicket()}</h3>
      <h4>${LangModalErrorHandler.noteCorruption()}</h4>
      <h4>Error Count = ${this.errors.length}</h4>
      ${this.errors.map((e, i) => this.renderApiError(e, i))}`;
  }
}
